'use client';

import useIsMobile from '@/hooks/useIsMobile';
import { MobileAdStripSectionProps } from '@/widgets/landing-page-widgets/MobileAdStrip/schema';
import { cn } from '@admin/lib/utils';
import { Typography } from '@pw-tech/omni-ui';
import { useInView } from 'framer-motion';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import AppStore from './AppStore';
import Playstore from './Playstore';
import styles from './styles.module.css';

const MobileAdStrip = ({ mobileAdStripData }: { mobileAdStripData: MobileAdStripSectionProps }) => {
  const { isMobile } = useIsMobile();
  const [isStripVisible, setIsStripVisible] = useState(false);
  const stripSectionRef = React.useRef<HTMLDivElement>(null);
  const isStripInView = useInView(stripSectionRef, { amount: 0.1 });

  useEffect(() => {
    if (isStripInView && !isStripVisible) {
      setIsStripVisible(true);
    }
  }, [isStripInView, isStripVisible]);

  return (
    <div className="w-full md:max-h-[400px] overflow-hidden" ref={stripSectionRef}>
      <div className="pt-6 pb-0 md:pt-16 bg-body-grey-6 px-4 overflow-hidden">
        <div className="mx-auto max-w-6xl px-4 md:px-6 flex flex-col md:flex-row md:justify-between gap-8 md:gap-0">
          <div className="flex flex-col gap-4 md:gap-8 md:max-w-md max-w-full">
            <div className="flex flex-col gap-2 md:gap-3 items-center md:items-start">
              <Typography
                variant="heading1"
                weight="semi-bold"
                className="text-center md:text-start"
              >
                {mobileAdStripData.heading}
              </Typography>
              <Typography
                variant="subHeading"
                weight="semi-bold"
                className="text-center md:text-start"
                color="#3d3d3d"
              >
                {mobileAdStripData.subHeading}
              </Typography>
            </div>
            <div className="flex flex-row justify-center md:justify-start gap-4">
              {mobileAdStripData.showPlaystore && (
                <button aria-label="Download on Play Store">
                  <a
                    href={mobileAdStripData.playStoreUrl}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Download on Play Store"
                  >
                    <Playstore height={isMobile ? 40 : 56} />
                  </a>
                </button>
              )}
              {mobileAdStripData?.showAppStore && (
                <button aria-label="Download on App Store" className="h-10 md:h-14">
                  <a
                    href={mobileAdStripData.appStoreUrl}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Download on App Store"
                  >
                    <AppStore width={isMobile ? 138 : 190} height={isMobile ? 40 : 56} />
                  </a>
                </button>
              )}
            </div>
          </div>
          <div
            className={cn(
              'max-w-[270px] md:max-w-[360px] flex justify-center items-center mx-auto md:mx-0 relative -bottom-14 md:-bottom-0',
              isStripVisible && styles.mobile_section
            )}
          >
            <Image
              src={mobileAdStripData.mobileBanner}
              layout="intrinsic"
              width={360}
              height={420}
              alt="mobile app banner"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileAdStrip;
